import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toast';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Custom hook for token-based invitation validation
 * Provides secure access control for registration page
 * 
 * Features:
 * - Extracts token from URL query parameters
 * - Validates token with backend API
 * - Manages loading, success, and error states
 * - Automatic redirection for invalid tokens
 * - Toast notifications for user feedback
 * 
 * @returns {Object} { isValidating, tokenData, error, isTokenValid }
 */
const useTokenValidation = (options = {}) => {
  const { requireToken = true } = options;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const validateInvitationToken = async () => {
      try {
        setIsValidating(true);
        setError(null);

        const token = searchParams.get('token');
        
        // If no token and token is not required, skip validation (open registration)
        if (!token && !requireToken) {
          setIsValidating(false);
          return;
        }
        
        // If no token but token is required, redirect
        if (!token && requireToken) {
          toast.error('Registration requires a valid invitation token');
          navigate('/login', { replace: true });
          return;
        }

        // Validate token with backend
        const response = await axios.post(
          `${API_BASE_URL}/users/decode_invite_token?token=${token}`,
          {
            timeout: 10000, // 10 second timeout for better UX
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        // Check for successful response
        if (response.data.code === 200 && response.data.email) {
          setTokenData({
            email: response.data.email,
            token: token,
            message: response.data.message
          });
          toast.success('Welcome! Please complete your registration.');
        } else {
          throw new Error('Invalid token response format');
        }

      } catch (err) {
        console.error('Token validation failed:', err);
        
        // Extract meaningful error message
        const errorMessage = err.response?.data?.detail || 
                           err.response?.data?.message || 
                           err.message ||
                           'Invalid or expired invitation token';
        
        setError(errorMessage);
        toast.error(errorMessage);
        
        // Delayed redirect for better UX (allows user to read error message)
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2500);
        
      } finally {
        setIsValidating(false);
      }
    };

    validateInvitationToken();
  }, [searchParams, navigate, requireToken]); // Dependencies for useEffect

  return { 
    isValidating, 
    tokenData, 
    error,
    // Utility function to check if token is valid
    isTokenValid: !error && !!tokenData 
  };
};

export default useTokenValidation;